// services/ideasVault.service.ts
import { api } from './api'; // Centralized API client
import { Idea, CreateIdeaData, UpdateIdeaData } from '../store/ideasStore';
import type { ApiResponse } from '../utils/types';
import { toast } from 'sonner';

// API endpoints
const API_ENDPOINTS = {
    ideas: '/ideas',
    idea: (id: string) => `/ideas/${id}`,
    export: '/ideas/export',
    stats: '/ideas/stats',
    aiSuggestions: '/ideas/ai-suggestions',
    categories: '/ideas/categories',
};

// Type guard for error with response
function isErrorWithResponse(error: unknown): error is { response?: { status?: number; data?: { message?: string } } } {
    return typeof error === 'object' && error !== null && 'response' in error;
}

// Ideas Service
export class IdeasService {
    // Get all ideas
    async getAll(filters?: {
        searchQuery?: string | undefined;
        category?: string | undefined;
        priority?: string | undefined;
        status?: string | undefined;
    }): Promise<Idea[]> {
        try {
            const params: Record<string, string> = {};
            

            if (filters?.searchQuery) params.q = filters.searchQuery;
            if (filters?.category && filters.category !== 'all') params.category = filters.category;
            if (filters?.priority && filters.priority !== 'all') params.priority = filters.priority;
            if (filters?.status && filters.status !== 'all') params.status = filters.status;

            const cleanedParams = Object.fromEntries(
                Object.entries(params).filter(([_, v]) => v !== undefined && v !== '')
            );

            console.log('API Request params:', cleanedParams);

            const response = await api.get<ApiResponse<Idea[]>>(API_ENDPOINTS.ideas, {
                params: cleanedParams,
            });


            console.log('API Response:', response.data);

            if (!response.data.success) {
                throw new Error(response.data.message || 'Failed to fetch ideas');
            }

            const transformedIdeas = response.data.data.map(this.transformIdea);
            console.log('Transformed ideas:', transformedIdeas);

            return transformedIdeas;
        } catch (error) {
            console.error('Failed to fetch ideas:', error);
            throw new Error('Failed to fetch ideas. Please try again.');
        }
    }

    // Get idea by ID
    async getById(id: string): Promise<Idea | null> {
        try {
            const response = await api.get<ApiResponse<Idea>>(API_ENDPOINTS.idea(id));
            if (!response.data.success) throw new Error(response.data.message || 'Idea not found');
            return this.transformIdea(response.data.data);
        } catch (error) {
            console.error('Failed to fetch idea:', error);
            if (isErrorWithResponse(error) && error.response?.status === 404) return null;
            throw new Error('Failed to fetch idea. Please try again.');
        }
    }

    // Create new idea
    async create(data: CreateIdeaData): Promise<Idea> {
        try {
            const response = await api.post<ApiResponse<Idea>>(API_ENDPOINTS.ideas, data);
            if (!response.data.success) throw new Error(response.data.message || 'Failed to create idea');

            toast.success('Idea created successfully');
            return this.transformIdea(response.data.data);
        } catch (error) {
            console.error('Failed to create idea:', error);
            const message = isErrorWithResponse(error) && error.response?.data?.message
                ? error.response.data.message
                : 'Failed to create idea. Please try again.';
            toast.error(`${message}`);
            throw new Error(message);
        }
    }

    // Update idea
    async update(id: string, data: Partial<UpdateIdeaData>): Promise<Idea> {
        try {
            const response = await api.patch<ApiResponse<Idea>>(API_ENDPOINTS.idea(id), data);
            if (!response.data.success) throw new Error(response.data.message || 'Failed to update idea');

            toast.success('Idea updated successfully');
            return this.transformIdea(response.data.data);
        } catch (error) {
            console.error('Failed to update idea:', error);
            let message = 'Failed to update idea. Please try again.';
            if (isErrorWithResponse(error)) {
                if (error.response?.status === 404) message = 'Idea not found';
                else if (error.response?.data?.message) message = error.response.data.message;
            }
            toast.error(`${message}`);
            throw new Error(message);
        }
    }

    // Delete idea
    async delete(id: string): Promise<void> {
        try {
            const response = await api.delete<ApiResponse<void>>(API_ENDPOINTS.idea(id));
            if (!response.data.success) throw new Error(response.data.message || 'Failed to delete idea');

            toast.success('Idea deleted successfully');
        } catch (error) {
            console.error('Failed to delete idea:', error);
            let message = 'Failed to delete idea. Please try again.';
            if (isErrorWithResponse(error)) {
                if (error.response?.status === 404) message = 'Idea not found';
                else if (error.response?.data?.message) message = error.response.data.message;
            }
            toast.error(`${message}`);
            throw new Error(message);
        }
    }

    // Search ideas
    async search(query: string): Promise<Idea[]> {
        try {
            const response = await api.get<ApiResponse<Idea[]>>(`${API_ENDPOINTS.ideas}?q=${encodeURIComponent(query)}`);
            if (!response.data.success) throw new Error(response.data.message || 'Failed to search ideas');
            return response.data.data.map(this.transformIdea);
        } catch (error) {
            console.error('Failed to search ideas:', error);
            throw new Error('Failed to search ideas. Please try again.');
        }
    }

    // Get ideas by category
    async getByCategory(category: string): Promise<Idea[]> {
        try {
            const response = await api.get<ApiResponse<Idea[]>>(`${API_ENDPOINTS.ideas}?category=${encodeURIComponent(category)}`);
            if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch ideas by category');
            return response.data.data.map(this.transformIdea);
        } catch (error) {
            console.error('Failed to fetch ideas by category:', error);
            throw new Error('Failed to fetch ideas by category. Please try again.');
        }
    }

    // Get AI suggestions
    async getAISuggestions(topics?: string[]): Promise<string[]> {
        try {
            const params = new URLSearchParams();
            if (topics && topics.length > 0) {
                topics.forEach(topic => params.append('topics', topic));
            }

            const queryString = params.toString();
            const url = queryString ? `${API_ENDPOINTS.aiSuggestions}?${queryString}` : API_ENDPOINTS.aiSuggestions;

            const response = await api.get<ApiResponse<string[]>>(url);
            if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch AI suggestions');

            return response.data.data;
        } catch (error) {
            console.error('Failed to fetch AI suggestions:', error);
            throw new Error('Failed to fetch AI suggestions. Please try again.');
        }
    }

    // Get stats
    async getStats(): Promise<any> {
        try {
            const response = await api.get<ApiResponse<any>>(API_ENDPOINTS.stats);
            if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch statistics');
            return response.data.data;
        } catch (error) {
            console.error('Failed to fetch idea statistics:', error);
            throw new Error('Failed to fetch idea statistics. Please try again.');
        }
    }

    // Get categories with counts
    async getCategories(): Promise<{ category: string; count: number }[]> {
        try {
            const response = await api.get<ApiResponse<{ category: string; count: number }[]>>(API_ENDPOINTS.categories);
            if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch categories');
            return response.data.data;
        } catch (error) {
            console.error('Failed to fetch categories:', error);
            throw new Error('Failed to fetch categories. Please try again.');
        }
    }

    // Export data
    async export(format: 'csv' | 'json' = 'json'): Promise<Blob> {
        try {
            const response = await api.get(`${API_ENDPOINTS.export}?format=${format}`, { responseType: 'blob' });
            return response.data;
        } catch (error) {
            console.error('Failed to export ideas:', error);
            throw new Error('Failed to export ideas. Please try again.');
        }
    }

    // Helper: normalize data
    private transformIdea(idea: any): Idea {
        return {
            ...idea,
            id: idea.id || idea._id,
            createdAt: new Date(idea.createdAt || new Date()),
            tags: Array.isArray(idea.tags) ? idea.tags : [],
            targetAudience: Array.isArray(idea.targetAudience) ? idea.targetAudience : [],
            aiGenerated: Boolean(idea.aiGenerated),
            status: idea.status || 'new',
            priority: idea.priority || 'medium',
            category: idea.category || 'video',
            estimatedEffort: Number(idea.estimatedEffort) || 0,
            description: idea.description || '',
        };
    }
}

// Singleton instance
export const ideasService = new IdeasService();